/*
 * hyperion-index:begin
 * ---
 * path: hyperion-cloud/web/src/platform/design-system/components/ui/PageHeader.tsx
 * layer: web
 * area: components
 * kind: component
 * web_root: components
 * relpath: web/src/platform/design-system/components/ui/PageHeader.tsx
 * filename: PageHeader.tsx
 * extension: tsx
 * ---
 * hyperion-index:end
 */

/**
 * P11 — PageHeader (eyebrow / title / subtitle / liveCounts / actions).
 *
 * Port of `hyperion-uxd/src/primitives-p11-header.jsx`. Composes the V3
 * chrome quintet documented in `docs/ux-toolkit-2026-04-30.md §3`. The
 * eyebrow is the only accent-tinted element; title and subtitle stay
 * neutral so headers read consistently across workflow groups.
 *
 * Accent is selected by the `accent` prop (one of the five workflow-group
 * keys). Consumers that want route-driven accent should pass
 * `useWorkflowGroup().tailwindColor`-derived value or call sites that
 * already use the named groups can pass them directly.
 */

import { createContext, Fragment, useContext, type ReactNode } from 'react';
import { cn } from '@/platform/shared/lib/utils';

export type PageHeaderAccent = 'amber' | 'violet' | 'emerald' | 'cyan' | 'blue';

const HeadingLevel = createContext<1 | 2>(1);

/** Embedded workspaces keep their own header while the enclosing page owns h1. */
export function PageHeaderScope({ children }: { children: ReactNode }) {
  return <HeadingLevel.Provider value={2}>{children}</HeadingLevel.Provider>;
}

interface AccentClasses {
  eyebrow: string;
}

const ACCENT_CLASSES: Record<PageHeaderAccent, AccentClasses> = {
  amber: { eyebrow: 'text-amber-700 dark:text-amber-300/80' },
  violet: { eyebrow: 'text-violet-700 dark:text-violet-300/80' },
  emerald: { eyebrow: 'text-emerald-700 dark:text-emerald-300/80' },
  cyan: { eyebrow: 'text-cyan-700 dark:text-cyan-300/80' },
  blue: { eyebrow: 'text-blue-700 dark:text-blue-300/80' },
};

export interface PageHeaderProps {
  /** Crumb segments — ["Library","Branding","Fonts"]. */
  eyebrow?: ReactNode[];
  title: ReactNode;
  titleId?: string;
  subtitle?: ReactNode;
  /** "42 active · 8 archived · last sync 3m" — accepts ReactNode for live dot inline. */
  liveCounts?: ReactNode;
  /** Right-aligned slot — typically <PHButton>s. */
  actions?: ReactNode;
  /** Beta / Soon / Alpha pill next to the title. */
  titleBadge?: ReactNode;
  /** Eyebrow accent. Defaults to amber (`detect-resolve`). */
  accent?: PageHeaderAccent;
  /** Adds the bottom border + extra padding seen on root settings pages. */
  divider?: boolean;
  className?: string;
}

export function PageHeader({
  eyebrow,
  title,
  titleId,
  subtitle,
  liveCounts,
  actions,
  titleBadge,
  accent = 'amber',
  divider = false,
  className,
}: PageHeaderProps) {
  const palette = ACCENT_CLASSES[accent];
  const Title = useContext(HeadingLevel) === 1 ? 'h1' : 'h2';
  return (
    <header
      className={cn(
        'mb-6',
        divider && 'pb-5 border-b border-slate-200 dark:border-white/10',
        className
      )}
    >
      {eyebrow && eyebrow.length > 0 && (
        <div className={cn('text-[11px] uppercase tracking-wider mb-2', palette.eyebrow)}>
          {eyebrow.map((seg, i) => (
            <Fragment key={i}>
              <span>{seg}</span>
              {i < eyebrow.length - 1 && (
                <span aria-hidden className="mx-1.5 text-slate-300 dark:text-slate-600">
                  /
                </span>
              )}
            </Fragment>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <Title
            id={titleId}
            className="min-w-0 break-words text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100"
          >
            {title}
          </Title>
          {titleBadge}
        </div>
        {actions && <div className="flex max-w-full flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {subtitle && (
        <p className="text-[13px] text-slate-500 dark:text-slate-400 max-w-3xl mt-1.5">
          {subtitle}
        </p>
      )}
      {liveCounts && (
        <div className="text-[11.5px] text-slate-400 dark:text-slate-500 mt-2">{liveCounts}</div>
      )}
    </header>
  );
}

// ---------------------------------------------------------------------
// PageHeader companions — PHButton / PHBadge
// ---------------------------------------------------------------------

export type PHButtonVariant = 'primary' | 'secondary' | 'ghost';
export interface PHButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: PHButtonVariant;
}

const PH_BUTTON_VARIANT: Record<PHButtonVariant, string> = {
  primary:
    'bg-slate-900 text-white hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100',
  secondary:
    'bg-white text-slate-800 ring-1 ring-slate-200 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-200 dark:ring-white/10 dark:hover:bg-slate-800',
  ghost: 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/5',
};

export function PHButton({ children, variant = 'primary', className, ...rest }: PHButtonProps) {
  return (
    <button
      className={cn(
        'h-8 px-3 rounded-md text-[12.5px] font-medium transition-colors inline-flex items-center gap-1.5',
        PH_BUTTON_VARIANT[variant],
        className
      )}
      {...rest}
      data-uix-control="page-header"
      aria-hidden={false}
      tabIndex={0}
      role="button"
    >
      {children}
    </button>
  );
}

export type PHBadgeTone = 'beta' | 'soon' | 'alpha';
export interface PHBadgeProps {
  tone?: PHBadgeTone;
  children: ReactNode;
  className?: string;
}

const PH_BADGE_TONE: Record<PHBadgeTone, string> = {
  beta: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
  soon: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  alpha: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
};

export function PHBadge({ tone = 'beta', children, className }: PHBadgeProps) {
  return (
    <span
      className={cn(
        'px-1.5 py-0.5 text-[10px] uppercase tracking-wider rounded font-medium',
        PH_BADGE_TONE[tone],
        className
      )}
    >
      {children}
    </span>
  );
}
